import { useEffect, useRef, useState } from 'react';
import { ApiError, CROCKFORD_RE, api, describeLinkError, formatLinkCode, normalizeLinkCode } from '../api/rest';
import type { LinkResponse } from '../api/protocol';
import { CodeInput } from '../components/CodeInput';
import { Icon } from '../components/Icon';
import { BRAND, Logo } from '../components/Logo';
import { Notice } from '../components/Notice';
import { Avatar } from '../components/Avatar';
import { scrubLinkCode } from '../router';

/** Codes are single-use: never send the same one twice (React StrictMode double mount). */
const attempts = new Map<string, Promise<LinkResponse>>();

type State =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; me: LinkResponse }
  | { kind: 'error'; message: string };

export function LinkPage({
  initialCode = null,
  notice,
  onLinked,
}: {
  /** code carried by the chat link (`/link#c=...`): submitted automatically */
  initialCode?: string | null;
  notice?: string | null;
  onLinked: (me: LinkResponse) => void;
}) {
  const [code, setCode] = useState(() => (initialCode ? normalizeLinkCode(initialCode).slice(0, 8) : ''));
  const [state, setState] = useState<State>({ kind: 'idle' });
  const doneRef = useRef(false);

  // Not tied to props: scrubbing the code from the URL must not cancel a request in flight.
  const submit = async (value = code) => {
    const c = normalizeLinkCode(value);
    if (!CROCKFORD_RE.test(c)) return;
    setState({ kind: 'submitting' });
    let p = attempts.get(c);
    if (!p) attempts.set(c, (p = api.link(c)));
    try {
      const me = await p;
      setState({ kind: 'success', me });
      if (!doneRef.current) {
        doneRef.current = true;
        window.setTimeout(() => onLinked(me), 900);
      }
    } catch (e) {
      attempts.delete(c);
      setState({ kind: 'error', message: describeLinkError(e) });
      if (e instanceof ApiError && e.code === 'INVALID_OR_EXPIRED') setCode('');
    }
  };

  useEffect(() => {
    if (!initialCode) return;
    scrubLinkCode();
    if (CROCKFORD_RE.test(code)) void submit(code);
    else setState({ kind: 'error', message: 'Link non valido. Scrivi di nuovo /voice in gioco oppure inserisci il codice.' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const complete = CROCKFORD_RE.test(code);

  return (
    <main className="link-page" data-testid="link-page">
      <div className="link-hero">
        <Logo variant="full" size={96} className="hero-logo" />
        <p className="hero-kicker">
          {BRAND.product} · <a href={BRAND.url} target="_blank" rel="noopener noreferrer">{BRAND.domain}</a>
        </p>
        <h1 className="display">
          Parla con chi
          <br />
          ti sta <em>vicino</em>.
        </h1>
        <p className="lead">
          La voice chat di prossimità del network Minecraft Titanet, direttamente nel browser. Senti gli altri giocatori da dove si trovano:
          più sono lontani, più la voce si affievolisce.
        </p>
        <p className="hero-cta" data-testid="link-cta">
          Entra su <strong>{BRAND.domain}</strong> e scrivi <kbd>/voice</kbd>
        </p>
        <ol className="steps">
          <li>
            <span className="step-n">1</span>
            <span>
              Entra in Minecraft sul server <strong>{BRAND.domain}</strong>.
            </span>
          </li>
          <li>
            <span className="step-n">2</span>
            <span>
              Scrivi <kbd>/voice</kbd> in chat.
            </span>
          </li>
          <li>
            <span className="step-n">3</span>
            <span>
              Clicca <strong>[Apri la voice chat]</strong> in chat, oppure inserisci qui il codice che vedi sotto il link.
            </span>
          </li>
        </ol>
      </div>

      <section className="card link-card" aria-labelledby="link-title">
        {notice ? <Notice tone="warn" title={notice} /> : null}

        {state.kind === 'success' ? (
          <div className="link-success" data-testid="link-success" role="status">
            <Avatar uuid={state.me.uuid} name={state.me.name} size={64} />
            <div>
              <p className="eyebrow">Collegato</p>
              <p className="link-success-name">{state.me.name}</p>
              <p className="muted">Apro la voice chat…</p>
            </div>
          </div>
        ) : state.kind === 'submitting' && initialCode ? (
          <div className="link-progress" role="status" data-testid="link-progress">
            <span className="loader" aria-hidden />
            <p>Collegamento in corso…</p>
          </div>
        ) : (
          <form
            className="link-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <h2 id="link-title">Inserisci il codice</h2>
            <p className="muted">8 caratteri, come {formatLinkCode('K7QF2MXR')}. Maiuscole e trattino non sono obbligatori.</p>
            <CodeInput
              value={code}
              onChange={(v) => {
                setCode(v);
                if (state.kind === 'error') setState({ kind: 'idle' });
              }}
              onComplete={(v) => void submit(v)}
              disabled={state.kind === 'submitting'}
              invalid={state.kind === 'error'}
            />
            {state.kind === 'error' ? (
              <p className="form-error" role="alert" data-testid="link-error">
                <Icon name="warn" size={14} /> {state.message}
              </p>
            ) : null}
            <button type="submit" className="btn btn-primary btn-block" disabled={!complete || state.kind === 'submitting'} data-testid="link-submit">
              {state.kind === 'submitting' ? 'Collegamento…' : 'Collega'}
            </button>
            <p className="fineprint">
              Il codice vale 5 minuti e si usa una sola volta. Non condividerlo: chi lo usa parla con il tuo nome. Se non funziona, scrivi di
              nuovo <kbd>/voice</kbd>.
            </p>
          </form>
        )}
      </section>
    </main>
  );
}
